import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, ArrowRight, ChevronRight, Loader2 } from 'lucide-react';
import { useEffect, useCallback } from 'react';
import { useBlogPost, useBlogCategories } from '../hooks/useBlog';
import useSEO from '../hooks/useSEO';

function useInternalLinkHandler(navigate) {
  return useCallback((e) => {
    const anchor = e.target.closest('a[href]');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (href && href.startsWith('/') && !href.startsWith('//')) {
      e.preventDefault();
      navigate(href);
    }
  }, [navigate]);
}

function RenderContent({ content, onLinkClick }) {
  const renderBlock = (block, i, level = 0) => {
    switch (block.type) {
      case 'p':
      case 'intro':
      case 'conclusion':
        return <p key={i} className={`text-zinc-300 leading-relaxed mb-4 ${block.type === 'intro' ? 'text-lg' : ''}`} onClick={onLinkClick} dangerouslySetInnerHTML={{ __html: block.text || '' }} />;
      case 'h2':
        return <h2 key={i} className="text-2xl font-semibold text-white mt-10 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{block.text}</h2>;
      case 'h3':
        return <h3 key={i} className="text-xl font-semibold text-white mt-8 mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{block.text}</h3>;
      case 'section':
        return (
          <section key={i} className="mb-2">
            {block.h2 && <h2 className="text-2xl font-semibold text-white mt-10 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{block.h2}</h2>}
            {block.text && <p className="text-zinc-300 leading-relaxed mb-4" onClick={onLinkClick} dangerouslySetInnerHTML={{ __html: block.text }} />}
            {(block.children || []).map((c, j) => renderBlock(c, `${i}-${j}`, level + 1))}
          </section>
        );
      case 'subsection':
        return (
          <div key={i}>
            {block.h3 && <h3 className="text-xl font-semibold text-white mt-8 mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{block.h3}</h3>}
            {block.text && <p className="text-zinc-300 leading-relaxed mb-4" onClick={onLinkClick} dangerouslySetInnerHTML={{ __html: block.text }} />}
            {(block.children || []).map((c, j) => renderBlock(c, `${i}-${j}`, level + 1))}
          </div>
        );
      case 'ul':
        return (
          <ul key={i} className="list-disc list-inside space-y-2 mb-4 pl-2">
            {(block.items || []).map((item, j) => (
              <li key={j} className="text-zinc-300 leading-relaxed" onClick={onLinkClick} dangerouslySetInnerHTML={{ __html: item }} />
            ))}
          </ul>
        );
      case 'ol':
        return (
          <ol key={i} className="list-decimal list-inside space-y-2 mb-4 pl-2">
            {(block.items || []).map((item, j) => (
              <li key={j} className="text-zinc-300 leading-relaxed" onClick={onLinkClick} dangerouslySetInnerHTML={{ __html: item }} />
            ))}
          </ol>
        );
      case 'cta': {
        const url = block.link || block.url || '/listings';
        return (
          <div key={i} className="mt-8 mb-4">
            <Link to={url} className="cta-button inline-flex items-center gap-2 bg-[#C5A059] hover:bg-[#b8933f] text-zinc-950 font-semibold px-6 py-3 text-sm transition-colors rounded-sm no-underline" data-testid="article-cta">
              {block.text} <ArrowRight size={16} />
            </Link>
          </div>
        );
      }
      case 'quote':
        return <blockquote key={i} className="border-l-4 border-[#C5A059] pl-4 italic text-zinc-400 my-6" onClick={onLinkClick} dangerouslySetInnerHTML={{ __html: block.text || '' }} />;
      default:
        // Graceful fallback for unknown block types with text
        if (block.text) return <p key={i} className="text-zinc-300 leading-relaxed mb-4" onClick={onLinkClick} dangerouslySetInnerHTML={{ __html: block.text }} />;
        return null;
    }
  };
  return content.map((block, i) => renderBlock(block, i, 0));
}

export default function BlogArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { post: article, related, loading, notFound, redirect } = useBlogPost(slug);
  const { categories } = useBlogCategories();
  const category = article ? categories.find(c => c.slug === article.category) : null;
  const handleLinkClick = useInternalLinkHandler(navigate);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Phase 4: honor 301 redirects from the API
  useEffect(() => {
    if (redirect) {
      // Internal (starts with '/') → SPA nav; external → hard redirect
      if (redirect.startsWith('/')) navigate(redirect, { replace: true });
      else window.location.replace(redirect);
    }
  }, [redirect, navigate]);

  useSEO({
    title: article ? `${article.title} — Blog AeroDeals` : 'Articolo — AeroDeals',
    description: article?.metaDescription || '',
    image: article?.image,
    type: 'article',
    keywords: article ? `${article.title.toLowerCase()}, aviazione, aerodeals` : '',
    jsonLd: article ? {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: article.title,
      description: article.metaDescription,
      image: article.image,
      datePublished: article.date,
      dateModified: article.updated_at || article.date,
      author: { '@type': 'Organization', name: 'AeroDeals' },
      publisher: {
        '@type': 'Organization',
        name: 'AeroDeals',
        logo: { '@type': 'ImageObject', url: 'https://www.aerodeals.it/logo-header.png' },
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': `https://www.aerodeals.it/blog/${slug}` },
    } : null,
  });

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | AeroDeals Blog`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', article.metaDescription);
    }
  }, [article]);

  if (loading || redirect) {
    return (
      <div className="min-h-screen bg-[#09090B] pt-20 flex items-center justify-center" data-testid="blog-article-loading">
        <Loader2 className="animate-spin text-zinc-500" size={20} />
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen bg-[#09090B] pt-20 flex items-center justify-center" data-testid="blog-article-notfound">
        <div className="text-center">
          <h1 className="text-3xl text-white mb-4">Articolo non trovato</h1>
          <Link to="/blog" className="text-[#C5A059] hover:underline">Torna al Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] pt-20" data-testid="blog-article-page">
      {/* Breadcrumb */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
        <nav className="flex items-center gap-2 text-xs text-zinc-500" data-testid="article-breadcrumb">
          <Link to="/" className="hover:text-zinc-300 transition-colors">Home</Link>
          <ChevronRight size={12} />
          <Link to="/blog" className="hover:text-zinc-300 transition-colors">Blog</Link>
          <ChevronRight size={12} />
          {category && (
            <>
              <Link to={`/blog?categoria=${article.category}`} className="hover:text-zinc-300 transition-colors">{category.name}</Link>
              <ChevronRight size={12} />
            </>
          )}
          <span className="text-zinc-400 truncate max-w-[200px]">{article.title}</span>
        </nav>
      </div>

      {/* Article Header */}
      <header className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="flex items-center gap-3 mb-4">
          {category && (
            <Link to={`/blog?categoria=${article.category}`} className="text-[10px] font-mono uppercase tracking-wider text-[#C5A059] border border-[#C5A059]/30 px-3 py-1 hover:bg-[#C5A059]/10 transition-colors" data-testid="article-category-badge">
              {category.name}
            </Link>
          )}
          <span className="text-xs text-zinc-500 flex items-center gap-1"><Calendar size={12} /> {new Date(article.date).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
          <span className="text-xs text-zinc-500 flex items-center gap-1"><Clock size={12} /> {article.readTime}</span>
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-[2.5rem] leading-tight font-medium text-white mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="article-title">
          {article.title}
        </h1>
        <p className="text-zinc-400 text-base leading-relaxed">{article.metaDescription}</p>
      </header>

      {/* Hero Image */}
      {article.image && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
          <div className="aspect-[21/9] overflow-hidden border border-zinc-800">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
              loading="eager"
              data-testid="article-hero-image"
            />
          </div>
        </div>
      )}

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="prose-custom" data-testid="article-content">
          <RenderContent content={article.content || []} onLinkClick={handleLinkClick} />
        </div>
      </article>

      {/* Related Articles */}
      {related.length > 0 && (
        <section className="border-t border-zinc-800/50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-8">Articoli Correlati</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map(rel => (
                <Link key={rel.slug} to={`/blog/${rel.slug}`} className="group border border-zinc-800 bg-zinc-900/30 overflow-hidden hover:border-zinc-600 transition-all" data-testid={`related-article-${rel.slug}`}>
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={rel.image} alt={rel.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-5">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-[#C5A059]">{categories.find(c => c.slug === rel.category)?.name}</span>
                    <h3 className="text-sm font-medium text-white mt-2 group-hover:text-[#C5A059] transition-colors line-clamp-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{rel.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Back to Blog */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <button onClick={() => navigate('/blog')} className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="back-to-blog">
          <ArrowLeft size={14} /> Torna al Blog
        </button>
      </div>
    </div>
  );
}
